import { CheckCircle2, CircleX, Loader2, Play, Clapperboard, FlaskConical } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useRuleLabel } from "@/components/aiot/lab/RulePanel";
import { LabCertCard } from "@/components/aiot/lab/LabCert";

const Bar = ({ label, v, max, testid }) => (
  <div data-testid={testid}>
    <div className="flex justify-between text-xs font-semibold text-[#0B1F3A]"><span>{label}</span><span>{v}/{max}</span></div>
    <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#14B8A6] transition-[width] duration-700" style={{ width: `${(v / max) * 100}%` }} /></div>
  </div>
);

export const useReason = (lab) => {
  const { L, lang } = useLang();
  const label = useRuleLabel(lab);
  const nm = (d) => (lang === "hi" ? lab.devices[d]?.hi : lab.devices[d]?.en);
  return (r) => {
    if (!r.reason) return r.fired.length ? `${L("Done", "हुआ")}: ${r.fired.map(label).join(", ")}` : L("Correct: nothing happened, as it should.", "सही: कुछ नहीं हुआ, जैसा होना चाहिए।");
    const [k, x] = r.reason;
    if (k === "sensor") return L(`${nm(x)} is not working. Check where it is placed and how it is wired.`, `${nm(x)} काम नहीं कर रहा। जगह और वायरिंग जाँचिए।`);
    if (k === "actuator") return L(`${nm(x)} cannot act. Check its placement and wiring.`, `${nm(x)} काम नहीं कर पा रहा। जगह और वायरिंग जाँचिए।`);
    if (k === "forbid") return L(`Wrong reaction: a rule fired "${label(x)}".`, `ग़लत प्रतिक्रिया: एक रूल ने "${label(x)}" चला दिया।`);
    return L("No rule reacts to this situation correctly yet.", "अभी कोई रूल इस स्थिति पर सही प्रतिक्रिया नहीं देता।");
  };
};

export const TestPanel = ({ lab, res, busy, onRun, onReplay, onShowcase, err, cert, snap }) => {
  const { L, lang } = useLang();
  const reason = useReason(lab);
  return (
    <div data-testid="lab-test-panel">
      <p className="text-sm text-slate-600">{L("Run every scenario on your build. The server checks placement, wiring and rules, then the room plays each situation in 3D.", "अपने बिल्ड पर हर सीनारियो चलाइए। सर्वर जगह, वायरिंग और रूल जाँचता है, फिर कमरा हर स्थिति 3D में चलाता है।")}</p>
      <button type="button" onClick={onRun} disabled={busy} className="mt-3 w-full h-12 rounded-full bg-[#0F766E] text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#0B1F3A] disabled:opacity-60" data-testid="lab-run">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}{L("Run all tests", "सारे टेस्ट चलाएँ")}
      </button>
      {err && <p className="mt-2 text-sm font-semibold text-[#DC2626]" data-testid="lab-run-error">{err}</p>}
      {cert && <div className="mt-4"><LabCertCard lab={lab} cert={cert} snap={snap} /></div>}
      {res?.score === 100 && !res.saved && <p className="mt-3 rounded-xl bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 text-xs font-semibold text-[#92400E]" data-testid="lab-cert-locked">{L("Perfect build! Sign in as an enrolled diploma student and run it again to earn your certificate.", "परफ़ेक्ट बिल्ड! प्रमाणपत्र पाने के लिए एनरोल्ड डिप्लोमा छात्र के रूप में साइन इन करके फिर से चलाइए।")}</p>}
      {res && (
        <div className="mt-4 space-y-4" data-testid="lab-results">
          <div className="flex items-end gap-4">
            <div className="text-5xl font-black font-['Outfit'] text-[#0B1F3A]" data-testid="lab-score">{res.score}</div>
            <div className="text-xs text-slate-500 pb-1">/100 · {res.saved ? L(`best ${res.best}, counts in your result`, `सबसे अच्छा ${res.best}, रिज़ल्ट में गिना जाएगा`) : L("practice only, sign in as an enrolled student to save", "सिर्फ़ प्रैक्टिस, सेव करने के लिए एनरोल्ड स्टूडेंट के रूप में साइन इन करें")}</div>
          </div>
          <div className="space-y-2">
            <Bar label={L("Placement", "प्लेसमेंट")} v={res.parts.place} max={lab.weights.place} testid="lab-part-place" />
            <Bar label={L("Wiring", "वायरिंग")} v={res.parts.wire} max={lab.weights.wire} testid="lab-part-wire" />
            <Bar label={L("Scenarios", "सीनारियो")} v={res.parts.scenarios} max={lab.weights.scenarios} testid="lab-part-scenarios" />
          </div>
          <ul className="space-y-2">
            {res.scenarios.map((r) => {
              const s = lab.scenarios.find((x) => x.id === r.id);
              return (
                <li key={r.id} className={`rounded-xl border px-3 py-2.5 ${r.pass ? "border-[#A7F3D0] bg-[#ECFDF5]" : "border-[#FECACA] bg-[#FEF2F2]"}`} data-testid={`lab-scenario-${r.id}`}>
                  <div className="flex items-start gap-2">
                    {r.pass ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#059669] shrink-0" /> : <CircleX className="w-4 h-4 mt-0.5 text-[#DC2626] shrink-0" />}
                    <div className="flex-1 min-w-0"><div className="text-sm font-bold text-[#0B1F3A]">{lang === "hi" ? s.hi : s.en}</div><div className="text-[11px] text-slate-600">{reason(r)}</div></div>
                    <button type="button" onClick={() => onReplay(r)} className="shrink-0 p-1.5 rounded-full hover:bg-white" aria-label="Replay" data-testid={`lab-replay-${r.id}`}><Play className="w-4 h-4 text-[#0F766E]" /></button>
                  </div>
                </li>
              );
            })}
          </ul>
          <button type="button" onClick={onShowcase} className="w-full h-12 rounded-full bg-[#0B1F3A] text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#0F766E]" data-testid="lab-showcase-open">
            <Clapperboard className="w-4 h-4" />{L("Watch the cinematic walkthrough", "सिनेमैटिक वॉकथ्रू देखें")}
          </button>
        </div>
      )}
    </div>
  );
};
